import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Carousel, Header, Loader, Panel, Text } from '@gasbuddy/react-components';
import Amenity from '../Amenity';
import AmenityPropType from '../../prop-types/amenity';
import { noop } from '../../../lib/utils';

import styles from './AmenitiesList.module.css';

const cx = classnames.bind(styles);

export default function AmenitiesList({ amenitiesList, error, expanded, fetchAmenities, isFetching, stationId }) {
  const [shouldFetchAmenities, setShouldFetchAmenities] = useState(true);

  useEffect(() => {
    if (!amenitiesList.length && !error && shouldFetchAmenities) {
      if (isFetching) {
        setShouldFetchAmenities(false);
      } else {
        fetchAmenities(stationId);
      }
    }
  }, [amenitiesList, error, fetchAmenities, isFetching, shouldFetchAmenities, stationId]);

  useEffect(() => {
    // if the stationId changes, then we should re-fetch the amenities again
    setShouldFetchAmenities(true);
  }, [stationId]);

  if (error) {
    return (
      <Text as="p" color="midnight" italic>An error occurred retrieving amenities. Please try again later.</Text>
    );
  }

  if (!isFetching && !amenitiesList.length) {
    return (
      <Text as="p" color="midnight" italic>No amenities found.</Text>
    );
  }

  return (
    <div>
      {expanded && (
        <Header as="h3" className={cx('amenityHeader')}>
          Features
        </Header>
      )}
      <Panel className={cx('amenitiesPanel', { shadow: expanded })} color="white" bordered={expanded} rounded={expanded} padded={expanded}>
        {isFetching
          ? <Loader>Loading amenities for this station...</Loader>
          : (
            <Carousel className={cx('amenitiesCarousel')} padded="scrollOnly" scrollable>
              {amenitiesList.map(amenity => (
                <Amenity item={amenity} key={amenity.displayName} />
              ))}
            </Carousel>
          )
        }
      </Panel>
    </div>
  );
}

AmenitiesList.propTypes = {
  amenitiesList: PropTypes.arrayOf(AmenityPropType),
  error: PropTypes.string,
  expanded: PropTypes.bool,
  fetchAmenities: PropTypes.func,
  isFetching: PropTypes.bool,
  stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

AmenitiesList.defaultProps = {
  amenitiesList: [],
  error: undefined,
  expanded: true,
  fetchAmenities: noop,
  isFetching: false,
};
